import React, { useContext } from 'react';
import styled from 'styled-components/macro';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import {
    Body,
    Container,
    Content as DefaultContent,
    ContentContainer as DefaultContentContainer
} from '../../containers/DefaultStyles/Details';
import NextAction from '../../containers/Widget/NextAction';
import Overlay from '../../containers/Widget/Overlay';
import OverlayPartner from '../../containers/Widget/OverlayPartner';
import OverlayPublisher from '../../containers/Widget/OverlayPublisher';
import WidgetHeader from '../../containers/Widget/WidgetHeader';
import { Footer } from '../../containers/DefaultStyles/Shared';

import ShareDrawer from '../../components/ShareDrawer';
import { Title } from '../../components/Shared/TitleSuccess';
import {
    Description as AutoScaleDescription,
    Title as AutoScaleTitle
} from '../../containers/DefaultStyles/Shared';
import { isLongText } from '../../components/Shared/commonFunctions';

import {
    getNextAction,
    buildConditionalActionsAnswerHistory
} from '../../utils/nextActionUtil';
import { widthEm, isPortrait } from '../../utils/size';
import { getImageUrl } from '../../utils/imageLoading';
import ThemeContext from '../../utils/theming';
import { copyFor } from '../../copy/copy';

import FacebookAltShare from '../SocialShareFooter/FacebookAltShare';
import TwitterShare from '../SocialShareFooter/TwitterShare';

const Content = styled(DefaultContent)`
    ${() => widthEm() <= 30 && `display: inline;`};
    ${props => props.hideBorders && `margin-top: 0;`};
`;

const ContentContainer = styled(DefaultContentContainer)`
    margin-top: 0;
    height: 100%;
`;

const Description = styled.div`
    object-fit: contain;
    font-family: Lato;
    line-height: 1.45;
    color: #000;
    ${props => (props.hasDirectionsLink ? 'font-size: 14px;' : 'font-size: 16px;')};

    @media (max-width: 36.5em) {
        ${props => (props.hasDirectionsLink ? 'font-size: 13px;' : 'font-size: 14px;')};
    }

    @media (max-width: 30em) {
        ${props => (props.hasDirectionsLink ? 'font-size: 14px;' : 'font-size: 16px;')};
        line-height: 1.35;
    }
`;

const ThankYouText = styled.span`
    color: #333;

    a {
        color: ${({ theme }) => theme.colors.primary};
        text-decoration: none;
    }
`;
const IframeComponent = styled.div``;

const ImageContainer = styled.div`
    text-align: center;
    margin: 0 auto;
    width: auto;
    max-height: ${props =>
        !props.hasNextAction ? `calc(100% - 100px);` : `calc(100% - 50px);`};
`;

const Image = styled.img`
    max-height: 100%;
    max-width: 100%;
    margin: 0 auto;
`;

const AltEndCardSharingContainer = styled.div`
    display: flex;
    margin: ${props => (props.isShort ? '.5rem' : '1rem')} 0;
`;

const ThankYouImage = props => {
    if (props.endCardPictureLink) {
        return (
            <ImageContainer hasNextAction={props.hasNextAction}>
                <a
                    style={{ margin: '0 auto' }}
                    href={props.endCardPictureLink}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <Image src={props.endCardPictureUrl} />
                </a>
            </ImageContainer>
        );
    } else {
        return (
            <ImageContainer>
                <Image src={props.endCardPictureUrl} />
            </ImageContainer>
        );
    }
};
ThankYouImage.propTypes = {
    endCardPictureLink: PropTypes.string,
    endCardPictureUrl: PropTypes.string,
    hasNextAction: PropTypes.bool
};

const DefaultThankYouText = props => {
    if (props.enableAltEndCardSharing)
        return (
            <AutoScaleDescription
                scaleFactor={0.9}
                isLongText={isLongText(copyFor('sharing', 'altShareBody'), 120)}
                isExtraLongText={isLongText(copyFor('sharing', 'altShareBody'), 180)}
            >
                {copyFor('sharing', 'altShareBody')}
            </AutoScaleDescription>
        );
    else
        return (
            <span>
                {copyFor(
                    'endCardFeedback',
                    props.useLocalRepresentative ? 'contactRep' : 'changeHeadlines',
                    undefined,
                    undefined,
                    { peopleCount: props.peopleCount }
                )}
            </span>
        );
};

DefaultThankYouText.propTypes = {
    enableAltEndCardSharing: PropTypes.bool,
    useLocalRepresentative: PropTypes.bool,
    peopleCount: PropTypes.string
};

const ThankYouTitle = props => {
    if (
        (props.enableAltEndCardSharing || props.nextStepButton) &&
        props.thankYouTitle &&
        !props.forceLargeTitle
    ) {
        return (
            <AutoScaleTitle
                scaleFactor={props.activeActionType === 'recordvideo'? 0.6 : 0.8}
                isShortText={isLongText(props.thankYouTitle, 35)}
                isLongText={isLongText(props.thankYouTitle, 65)}
                isExtraLongText={isLongText(props.thankYouTitle, 95)}
            >
                {props.thankYouTitle}
            </AutoScaleTitle>
        );
    } else {
        return props.thankYouTitle ? (
            <Title>{props.thankYouTitle}</Title>
        ) : (
            props.titleComponent
        );
    }
};

ThankYouTitle.propTypes = {
    activeActionType: PropTypes.string,
    enableAltEndCardSharing: PropTypes.bool,
    thankYouTitle: PropTypes.string,
    titleComponent: PropTypes.object,
    nextStepButton: PropTypes.element,
    forceLargeTitle: PropTypes.bool
};

const ThumbnailComponent = styled.div`
    display: flex;
    justify-content: center;
    margin: ${() => (isPortrait() ? '0 0 10px 0;' : '6px 16px;')};
`;

const ConfirmationCard = props => {
    const theme = useContext(ThemeContext);
    return (
        <Container>
            <Overlay />
            <OverlayPublisher />
            <OverlayPartner transitionType="right-to-left" />
            <Body>
                {!props.hideBorders && <WidgetHeader />}
                <Content hideBorders={props.hideBorders}>
                    {props.fullAddress && (
                        <IframeComponent>{props.iframeComponent}</IframeComponent>
                    )}
                    {props.thumbnailComponent && (
                        <ThumbnailComponent>
                            {props.thumbnailComponent}
                        </ThumbnailComponent>
                    )}
                    <ContentContainer>
                        <ThankYouTitle
                            activeActionType={props.actionActionType}
                            enableAltEndCardSharing={props.enableAltEndCardSharing}
                            nextStepButton={props.nextStepButton}
                            thankYouTitle={props.thankYouTitle}
                            titleComponent={props.titleComponent}
                            forceLargeTitle={props.forceLargeTitle}
                        />
                        {props.hasImage && props.endCardPictureUrl && !props.hideImage ? (
                            <ThankYouImage
                                endCardPictureLink={props.endCardPictureLink}
                                endCardPictureUrl={props.endCardPictureUrl}
                            />
                        ) : (
                            <div>
                                <Description
                                    className="ie-object-fit-contain-polyfill"
                                    hasDirectionsLink={props.getDirectionsComponent}
                                >
                                    {!props.hideSubtitle && !props.thankYouText && (
                                        <DefaultThankYouText
                                            enableAltEndCardSharing={
                                                props.enableAltEndCardSharing
                                            }
                                            useLocalRepresentative={
                                                props.useLocalRepresentative
                                            }
                                            peopleCount={props.peopleCount}
                                        />
                                    )}
                                    {props.bodyComponent ? (
                                        props.bodyComponent
                                    ) : props.enableAltEndCardSharing ? (
                                        <AutoScaleDescription
                                            scaleFactor={0.9}
                                            isLongText={isLongText(
                                                props.thankYouText,
                                                120
                                            )}
                                            isExtraLongText={isLongText(
                                                props.thankYouText,
                                                180
                                            )}
                                            dangerouslySetInnerHTML={{
                                                __html: props.thankYouText
                                            }}
                                        />
                                    ) : (
                                        <ThankYouText
                                            theme={theme}
                                            dangerouslySetInnerHTML={{
                                                __html: props.thankYouText
                                            }}
                                        />
                                    )}
                                    {props.customBackButton}

                                    <div>
                                        {props.linkComponent}
                                        {props.enableAltEndCardSharing && (
                                            <AltEndCardSharingContainer
                                                isShort={props.altSharingButtonsAreShort}
                                            >
                                                <TwitterShare
                                                    isShort={
                                                        props.altSharingButtonsAreShort
                                                    }
                                                    isAltSharing={true}
                                                    altSharingURL={props.altSharingURL}
                                                    altSharingDefaultText={
                                                        props.altShareDefaultTwitterFacebookMessage
                                                    }
                                                    altSharingCustomText={
                                                        props.altShareCustomTwitterMessage
                                                    }
                                                />
                                                <FacebookAltShare
                                                    isShort={
                                                        props.altSharingButtonsAreShort
                                                    }
                                                    altSharingURL={props.altSharingURL}
                                                    altSharingDefaultText={
                                                        props.altShareDefaultTwitterFacebookMessage
                                                    }
                                                    altSharingCustomText={
                                                        props.altShareCustomFacebookMessage
                                                    }
                                                />
                                            </AltEndCardSharingContainer>
                                        )}
                                    </div>
                                </Description>
                                {props.fullAddress && props.getDirectionsComponent}
                            </div>
                        )}
                        {props.nextStepButton}
                    </ContentContainer>
                </Content>
                {!props.hideBorders && !props.hideFooter && (
                    <Footer>
                        <ShareDrawer
                            fullwidth={props.fullwidth}
                            rightAlign={props.rightAlign}
                            newsletterSignUpVisible={true}
                        />
                    </Footer>
                )}
            </Body>
            {!props.nextStepButton && (
                <NextAction
                    isEndCard={true}
                    hideFooter={props.hideBorders || props.hideFooter}
                    exitingAction={props.exitingAction}
                    beforeAdvancing={props.beforeAdvancing}
                    noCountdown={props.noCountdown}
                />
            )}
        </Container>
    );
};

ConfirmationCard.propTypes = {
    altSharingURL: PropTypes.string,
    altSharingButtonsAreShort: PropTypes.bool,
    beforeAdvancing: PropTypes.func,
    bodyComponent: PropTypes.element,
    altShareCustomTwitterMessage: PropTypes.string,
    altShareCustomFacebookMessage: PropTypes.string,
    endCardPictureUrl: PropTypes.string,
    endCardPictureLink: PropTypes.string,
    exitingAction: PropTypes.object.isRequired,
    fullAddress: PropTypes.string,
    fullwidth: PropTypes.bool,
    getDirectionsComponent: PropTypes.element,
    hasImage: PropTypes.bool,
    hideFooter: PropTypes.bool,
    hideBorders: PropTypes.bool,
    hideImage: PropTypes.bool,
    hideSubtitle: PropTypes.bool,
    iframeComponent: PropTypes.element,
    noCountdown: PropTypes.bool,
    peopleCount: PropTypes.string,
    rightAlign: PropTypes.bool,
    thankYouText: PropTypes.string,
    thankYouTitle: PropTypes.string,
    titleComponent: PropTypes.element,
    useLocalRepresentative: PropTypes.bool,
    enableAltEndCardSharing: PropTypes.bool,
    altShareDefaultTwitterFacebookMessage: PropTypes.string,
    linkComponent: PropTypes.element,
    thumbnailComponent: PropTypes.element,
    customBackButton: PropTypes.element,
    nextStepButton: PropTypes.element,
    forceLargeTitle: PropTypes.bool
};

export default connect(
    (state, props) => {
        const nextAction = getNextAction(
            state.widget.actions,
            props.exitingAction.identifier,
            buildConditionalActionsAnswerHistory(state),
            state.widget.cards
        );
        return {
            activeActionType: state.widget.activeAction.type,
            hasNextAction: !!nextAction,
            enableAltEndCardSharing:
                state.widget.activeAction.enableAltEndCardSharing &&
                !props.disableAltEndCardSharing,
            endCardPictureLink: props.exitingAction.endCardPictureLink,
            endCardPictureUrl: getImageUrl(props.exitingAction.endCardPictureId),
            hasImage: !!props.exitingAction.endCardPictureId,
            hideBorders: props.exitingAction.hideEndCardBorders,
            thankYouText: props.thankYouText || props.exitingAction.thankYouText,
            thankYouTitle: props.thankYouTitle || props.exitingAction.thankYouTitle,
            useLocalRepresentative:
                props.useLocalRepresentative || !!props.exitingAction.representativeSearch
        };
    },
    {}
)(ConfirmationCard);
